import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { assertNotNil } from '../../../../utils/types';
import {
  EntityRole,
  IDashboardFromTemplateQuery,
  IDashboardFromTemplateQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { dashboardFromTemplateQuery } from '../../../../utils/apollo/dashboards';
import useDashboardSelector from '../../../../redux/selectorHooks/useDashboardSelector';
import useUpdateDashboardStore from '../../utils/useUpdateDashboardStore';
import DashboardContent from '../components/DashboardContent';
import DashboardInstanceLoader from '../../components/DashboardInstanceLoader';
import * as EmbeddedDashboardUI from './EmbeddedDashboardViewUI';
import EntityRoleLabel from '../../../../components/SharePermissionsWidget/EntityRoleLabel';
import DashboardRefreshInfo from '../components/DashboardRefreshInfo';
import { dashboardViewed } from '../../../../utils/analytics/events/dashboard/events';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';

const EmbeddedDashboardView = () => {
  const { track } = useAnalytics();
  const { dashboardBaseId } = useParams();
  assertNotNil(dashboardBaseId);

  const isLoaded = useDashboardSelector('isLoaded');
  const updateDashboardStore = useUpdateDashboardStore();

  const handleLoadTemplate = ({
    dashboardFromTemplate: dashboard,
  }: IDashboardFromTemplateQuery) => {
    updateDashboardStore({
      dashboard,
    });

    track(dashboardViewed(dashboard, { isEmbedded: true }));
  };

  const { loading: queryLoading } = useQuery<
    IDashboardFromTemplateQuery,
    IDashboardFromTemplateQueryVariables
  >(dashboardFromTemplateQuery, {
    onCompleted: handleLoadTemplate,
    variables: {
      baseId: dashboardBaseId,
    },
  });

  const dashboard = useDashboardSelector('dashboard');

  if (!isLoaded || queryLoading) {
    return <DashboardInstanceLoader />;
  }

  if (!dashboard) {
    return null;
  }

  const { name, description, refreshedAt } = dashboard;

  return (
    <EmbeddedDashboardUI.Container>
      <EmbeddedDashboardUI.Header>
        <EmbeddedDashboardUI.HeaderMain>
          <div>
            <EmbeddedDashboardUI.Title>{name}</EmbeddedDashboardUI.Title>
            {description && (
              <EmbeddedDashboardUI.Description>
                {description}
              </EmbeddedDashboardUI.Description>
            )}
          </div>
          <EmbeddedDashboardUI.HeaderInfo>
            <EntityRoleLabel entityRole={EntityRole.Viewer} />
            {refreshedAt && <DashboardRefreshInfo refreshedAt={refreshedAt} />}
          </EmbeddedDashboardUI.HeaderInfo>
        </EmbeddedDashboardUI.HeaderMain>
      </EmbeddedDashboardUI.Header>
      <EmbeddedDashboardUI.Content>
        <DashboardContent disableContentUpdates={true} isEmbedded={true} />
      </EmbeddedDashboardUI.Content>
    </EmbeddedDashboardUI.Container>
  );
};

export default EmbeddedDashboardView;
